import { useState, useEffect, useMemo } from "react";
import styles from "../styles/pages/Admin.module.css";

import {
  UserCardRow,
  USER_BODY_COLUMNS,
  PermGroupCardRow,
  PERM_GROUP_COLUMNS,
} from "../ui/cards/AdminCards";
import SearchInput from "../ui/inputs/SearchInput";
import ChoiceChip from "../ui/selectors/ChoiceChip";
import Button from "../ui/buttons/Button";
import DropdownSelect from "../ui/inputs/DropdownSelect";

import { ReactComponent as Vector9Icon } from "../assets/images/vector_arrow.svg";
import { ReactComponent as AddIcon } from "../assets/icons/add.svg";

import { accountsService } from "../services/accounts.service";
import { permissionsService } from "../services/permissions.service";
import { useAuth } from "../contexts/AuthContext";
import { UserDetailModal, PermissionGroupDetailModal } from "../components/admin/AdminDetailModals";
import { getGrupo } from "../utils/getGrupo";

import LoadingScreen from "../ui/loadingScreen/LoadingScreen";

function HeaderCell({ children, column, align = "center" }) {
  return (
    <div
      style={{
        gridColumn: column,
        textAlign: align,
        color: "var(--color-text-text-primary)",
        fontSize: 14,
        fontFamily: "DM Sans",
        fontWeight: 500,
        lineHeight: "17px",
        minWidth: 0,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </div>
  );
}

function UsersTable({ rows, onSelect }) {
  if (!rows.length) {
    return (
      <div className={styles.emptyState}>No hay usuarios para mostrar</div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.table}>
        <div className={styles.headerContainer}>
          <div className={styles.headerBody}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: USER_BODY_COLUMNS,
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <HeaderCell column="1" align="left">
                Usuario
              </HeaderCell>
              <HeaderCell column="3">Email</HeaderCell>
              <HeaderCell column="5">Grupo</HeaderCell>
              <HeaderCell column="7">Nombre</HeaderCell>
              <HeaderCell column="9">Apellido</HeaderCell>
              <HeaderCell column="11">Estado</HeaderCell>
            </div>
          </div>
          <div className={styles.headerActionSpacer} />
        </div>

        {rows.map((user) => (
          <div
            key={`user-${user.id}`}
            className={styles.rowWrapper}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(user)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSelect(user);
              if (e.key === " ") {
                e.preventDefault();
                onSelect(user);
              }
            }}
          >
            <UserCardRow
              username={user.username ?? "-"}
              email={user.email ?? "-"}
              grupo={getGrupo(user)}
              firstName={user.first_name ?? "-"}
              lastName={user.last_name ?? "-"}
              isActive={user.is_active !== false}
              actionIcon={<Vector9Icon />}
              onAction={() => onSelect(user)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function PermGroupsTable({ rows, onSelect }) {
  if (!rows.length) {
    return (
      <div className={styles.emptyState}>
        No hay grupos de permisos para esta cuenta
      </div>
    );
  }
  return (
    <div className={styles.tableContainer}>
      <div className={styles.table}>
        <div className={styles.headerContainer}>
          <div className={styles.headerBody}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: PERM_GROUP_COLUMNS,
                alignItems: "center",
                minWidth: 0,
              }}
            >
              <HeaderCell column="1" align="left">
                Nombre
              </HeaderCell>
              <HeaderCell column="2" align="left">
                Descripción
              </HeaderCell>
              <HeaderCell column="3" align="center">
                Permisos
              </HeaderCell>
              <HeaderCell column="4" align="center">
                Miembros
              </HeaderCell>
            </div>
          </div>
          <div className={styles.headerActionSpacer} />
        </div>
        {rows.map((group) => (
          <div
            key={`group-${group.id}`}
            className={styles.rowWrapper}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(group)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSelect(group);
              if (e.key === " ") {
                e.preventDefault();
                onSelect(group);
              }
            }}
          >
            <PermGroupCardRow
              name={group.name}
              description={group.description}
              permissionCount={group.permission_count ?? 0}
              memberCount={group.member_count ?? 0}
              actionIcon={<Vector9Icon />}
              onAction={() => onSelect(group)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function PermisosTab({ rows, loadingGroups, onSelect }) {
  if (loadingGroups) return <LoadingScreen mainText="Cargando grupos..." />;
  return <PermGroupsTable rows={rows} onSelect={onSelect} />;
}

const AdminUsuariosPage = () => {
  const { supervisedAccounts } = useAuth();
  const [tab, setTab] = useState("usuarios");
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createMode, setCreateMode] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");

  useEffect(() => {
    const accountsArr = supervisedAccounts || [];
    setAccounts(accountsArr);
    if (accountsArr.length) setAccountId(String(accountsArr[0].id));
  }, [supervisedAccounts]);

  useEffect(() => {
    if (!supervisedAccounts?.length) {
      setUsers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all(supervisedAccounts.map((account) => accountsService.listUsers(account.id)))
      .then((results) => {
        const uniqueUsers = Array.from(
          new Map(results.flat().map((user) => [user.id, user])).values(),
        );
        setUsers(uniqueUsers);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [supervisedAccounts]);

  useEffect(() => {
    if (!accountId) {
      setGroups([]);
      return;
    }
    setLoadingGroups(true);
    permissionsService
      .listGroups(Number(accountId))
      .then((data) => setGroups(Array.isArray(data) ? data : []))
      .catch(() => setGroups([]))
      .finally(() => setLoadingGroups(false));
  }, [accountId]);

  const handleTabChange = (next) => {
    setTab(next);
    setSearch("");
    setShowCreateGroup(false);
  };

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const lower = search.toLowerCase();
    return users.filter(
      (u) =>
        u.username?.toLowerCase().includes(lower) ||
        u.email?.toLowerCase().includes(lower) ||
        u.first_name?.toLowerCase().includes(lower) ||
        u.last_name?.toLowerCase().includes(lower),
    );
  }, [users, search]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const lower = search.toLowerCase();
    return groups.filter(
      (g) =>
        g.name?.toLowerCase().includes(lower) ||
        g.description?.toLowerCase().includes(lower),
    );
  }, [groups, search]);

  if (loading) {
    return (
      <LoadingScreen
        mainText="Cargando usuarios..."
        subText="Por favor espera mientras cargamos los datos de los usuarios"
      />
    );
  }

  return (
    <div className={styles.admin}>
      <div className={styles.admin1}>
        <div className={styles.adminInner}>
          <div className={styles.frameParent}>
            <div className={styles.pageTitle}>Usuarios</div>

            <div className={styles.toolbar}>
              <div className={styles.toolbarLeft}>
                <SearchInput onChange={setSearch} width={241} height={40} />

                <div className={styles.filterGroup}>
                  <ChoiceChip
                    label="Usuarios"
                    selected={tab === "usuarios"}
                    onClick={() => handleTabChange("usuarios")}
                    height={40}
                    iconSize={0}
                    fontWeight={500}
                    selectedColor="var(--text-cta-primary)"
                    unselectedColor="var(--text-tertiary)"
                    selectedBackground="var(--surface-accent-hover)"
                    unselectedBackground="transparent"
                  />
                  <ChoiceChip
                    label="Permisos"
                    selected={tab === "permisos"}
                    onClick={() => handleTabChange("permisos")}
                    height={40}
                    iconSize={0}
                    fontWeight={500}
                    selectedColor="var(--text-cta-primary)"
                    unselectedColor="var(--text-tertiary)"
                    selectedBackground="var(--surface-accent-hover)"
                    unselectedBackground="transparent"
                  />
                </div>

                {tab === "permisos" && (
                  <div style={{ width: 200 }}>
                    <DropdownSelect
                      placeholder="Cuenta..."
                      options={accounts.map((a) => ({
                        value: String(a.id),
                        label: a.name ?? "-",
                      }))}
                      value={accountId}
                      onChange={setAccountId}
                    />
                  </div>
                )}
              </div>

              <Button
                type="button"
                iconLeft={<AddIcon />}
                width={148}
                height={40}
                onClick={() => {
                  if (tab === "permisos") {
                    setShowCreateGroup(true);
                  } else {
                    setCreateMode(true);
                    setSelectedUser(null);
                  }
                }}
                style={{
                  paddingTop: 10,
                  paddingBottom: 10,
                  paddingLeft: 8,
                  paddingRight: 16,
                  background: "var(--surface-accent-hover)",
                  color: "var(--text-cta-primary)",
                  fontSize: 13,
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  gap: 0,
                }}
              >
                {tab === "permisos" ? "Nuevo Grupo" : "Nuevo Usuario"}
              </Button>
            </div>

            {tab === "usuarios" ? (
              <UsersTable rows={filteredUsers} onSelect={setSelectedUser} />
            ) : (
              <PermisosTab
                rows={filteredGroups}
                loadingGroups={loadingGroups}
                onSelect={setSelectedGroup}
              />
            )}
          </div>
        </div>
      </div>

      <PermissionGroupDetailModal
        group={selectedGroup}
        createMode={showCreateGroup}
        accountId={accountId ? Number(accountId) : null}
        onClose={() => {
          setSelectedGroup(null);
          setShowCreateGroup(false);
        }}
        onSaved={(updated) => {
          setGroups((prev) =>
            prev.map((g) =>
              g.id === updated.id
                ? {
                    ...g,
                    name: updated.name,
                    description: updated.description,
                    permission_count:
                      updated.permissions?.length ?? g.permission_count,
                  }
                : g,
            ),
          );
          setSelectedGroup(null);
        }}
        onCreated={(group) => {
          setGroups((prev) => [
            ...prev,
            {
              ...group,
              permission_count: group.permissions?.length ?? 0,
              member_count: 0,
            },
          ]);
          setShowCreateGroup(false);
        }}
        onDeleted={(id) => {
          setGroups((prev) => prev.filter((g) => g.id !== id));
          setSelectedGroup(null);
        }}
      />

      <UserDetailModal
        user={selectedUser}
        createMode={createMode}
        onClose={() => {
          setSelectedUser(null);
          setCreateMode(false);
        }}
        onCreated={(newUser, code) => {
          if (code === "CREATED") setUsers((prev) => [...prev, newUser]);
          setCreateMode(false);
          setSelectedUser(newUser);
        }}
        onDeleted={(id) => {
          setUsers((prev) => prev.filter((u) => u.id !== id));
          setSelectedUser(null);
        }}
        onUserChanged={(updatedUser) => {
          setUsers((prev) =>
            prev.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
          );
          setSelectedUser(updatedUser);
        }}
      />
    </div>
  );
};

export default AdminUsuariosPage;
